'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { ThemeName, Theme, ThemeContextValue } from './types';
import { neobrutalismTheme, neobrutalismCSSVarMap } from './neobrutalism';
import { liquidGlassTheme, liquidGlassCSSVarMap } from './liquid-glass';
import type { ThemeCssVarMap } from './tokens';

// Theme configurations map
const themes: Record<ThemeName, Theme> = {
    neobrutalism: neobrutalismTheme,
    'liquid-glass': liquidGlassTheme,
};

const themeVars: Record<ThemeName, ThemeCssVarMap> = {
    neobrutalism: neobrutalismCSSVarMap,
    'liquid-glass': liquidGlassCSSVarMap,
};

// Default context value
const defaultContext: ThemeContextValue = {
    theme: 'neobrutalism',
    setTheme: () => { },
    themeConfig: neobrutalismTheme,
};

// Create context
const ThemeContext = createContext<ThemeContextValue>(defaultContext);

// Storage key
const THEME_STORAGE_KEY = 'dopomogai-ui-theme';

export interface ThemeProviderProps {
    children: React.ReactNode;
    /** Initial theme to use. Defaults to 'neobrutalism' */
    defaultTheme?: ThemeName;
    /** Whether to persist theme preference to localStorage */
    persist?: boolean;
    /** Force a specific theme (overrides persisted value) */
    forcedTheme?: ThemeName;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
    children,
    defaultTheme = 'neobrutalism',
    persist = true,
    forcedTheme,
}) => {
    const [theme, setThemeState] = useState<ThemeName>(forcedTheme ?? defaultTheme);

    // Apply CSS variables to document root
    const applyTheme = useCallback((themeName: ThemeName) => {
        if (typeof document === 'undefined') return;

        const root = document.documentElement;
        const cssVars = themeVars[themeName];
        Object.entries(cssVars).forEach(([key, value]) => {
            root.style.setProperty(key, value);
        });

        // Add theme class to body for conditional styling
        document.body.classList.remove('theme-neobrutalism', 'theme-liquid-glass');
        document.body.classList.add(`theme-${themeName}`);

        // Set data attribute for CSS selectors
        root.dataset.theme = themeName;
    }, []);

    // Set theme function exposed via context
    const setTheme = useCallback((newTheme: ThemeName) => {
        if (forcedTheme) return; // Don't allow changes if forced

        setThemeState(newTheme);
        applyTheme(newTheme);

        if (persist && typeof localStorage !== 'undefined') {
            localStorage.setItem(THEME_STORAGE_KEY, newTheme);
        }
    }, [forcedTheme, persist, applyTheme]);

    // Initialize theme on mount
    useEffect(() => {
        let initialTheme = forcedTheme ?? defaultTheme;

        if (!forcedTheme && persist && typeof localStorage !== 'undefined') {
            const stored = localStorage.getItem(THEME_STORAGE_KEY) as ThemeName | null;
            if (stored && themes[stored]) {
                initialTheme = stored;
            }
        }

        setThemeState(initialTheme);
        applyTheme(initialTheme);
    }, [forcedTheme, defaultTheme, persist, applyTheme]);

    // Memoize context value
    const contextValue = useMemo<ThemeContextValue>(() => ({
        theme,
        setTheme,
        themeConfig: themes[theme],
    }), [theme, setTheme]);

    return (
        <ThemeContext.Provider value={contextValue}>
            {children}
        </ThemeContext.Provider>
    );
};

// Hook to use theme
export const useTheme = (): ThemeContextValue => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

// Export themes for direct access if needed
export { themes };
